import { useNavigate } from 'react-router-dom';
import { Icon, Ring, SectionLabel, Badge, type IconName } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useCourses, useEnrollments, useCertificates } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';
import { CourseCard } from '../CourseCard';

type TodoItem = { n: number; l: string; ic: IconName; t: 'sage' | 'spark' };

export function DesktopHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { L } = useLocalized();
  const { data: courses = [] } = useCourses();
  const { data: enrollments = [] } = useEnrollments();
  const { data: certs = [] } = useCertificates();
  const validCerts = certs.filter((c) => c.status === 'valid').length;
  const expiringCerts = certs.filter((c) => c.status === 'expiring').length;
  const certPct = certs.length ? Math.round((validCerts / certs.length) * 100) : 0;

  // Any enrolled-but-not-finished course belongs in "Continue" — a fresh enrollee is
  // status 'enrolled', not 'in-progress', and must still anchor the left column.
  const active = courses.filter((c) => enrollments.some((e) => e.courseId === c.id && e.status !== 'completed'));
  const next = active[0];
  const nextEnr = next && enrollments.find((e) => e.courseId === next.id);
  const allTodoItems: TodoItem[] = next && nextEnr ? [
    { n: next.lessons.filter((l) => !l.done).length, l: 'Lessons left', ic: 'video', t: 'sage' },
    { n: next.requirements.includes('posttest') && nextEnr.posttest == null ? 1 : 0, l: 'Post-test due', ic: 'doc', t: 'spark' },
    { n: next.requirements.includes('eval') && !nextEnr.eval ? 1 : 0, l: 'Evaluation', ic: 'star', t: 'sage' },
    { n: next.requirements.includes('attend') && !nextEnr.attended ? 1 : 0, l: 'Check-in', ic: 'qr', t: 'spark' },
  ] : [];
  const todoItems = allTodoItems.filter((x) => x.n > 0);
  const recommended = courses.filter((c) => c.status === 'active' && !enrollments.some((e) => e.courseId === c.id)).slice(0, 3);
  // New learner (nothing in progress): feature the first course so the left column isn't an empty void.
  const featured = !next ? recommended[0] : null;
  const restRec = featured ? recommended.slice(1) : recommended;
  const upcoming = courses.find((c) => (c.type === 'onsite' || c.type === 'hybrid') && !!c.seminar && enrollments.some((e) => e.courseId === c.id));
  const upcomingEnr = upcoming ? enrollments.find((e) => e.courseId === upcoming.id) : undefined;
  const attended = !!upcomingEnr?.attended;
  // Date-only, local-time comparison so check-in flips exactly on the session day.
  const checkinOpen = !upcoming?.seminarDate || new Date().toLocaleDateString('en-CA') >= upcoming.seminarDate;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 32 }}>
      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 20, padding: '26px 28px', marginBottom: 24, background: 'linear-gradient(118deg, var(--sage-tint) 0%, var(--card) 54%, var(--spark-tint) 100%)', border: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 8, color: 'var(--sage-deep)' }}>Welcome back</div>
            <h1 style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-.02em', margin: 0 }}>สวัสดี, {user?.lineName ?? user?.firstEn}</h1>
            <p style={{ margin: '10px 0 0', fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink-2)', maxWidth: 400 }}>
              {next ? (nextEnr?.progress ? `You're ${nextEnr.progress}% through ${next.title.en} — keep going.` : `Start ${next.title.en} — the pre-test unlocks your lessons.`) : 'Browse the catalog and start your next certification.'}
            </p>
          </div>
          <Badge kind="sage" dot>{user?.role} · {user?.unitId}</Badge>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {next && nextEnr && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ height: 8, background: next.cover }} />
            <div style={{ padding: 24 }}>
              <SectionLabel action={<button onClick={() => navigate('/learn/learning')} className="btn-quiet btn-sm" style={{ color: 'var(--sage-deep)', border: 'none', background: 'transparent', cursor: 'pointer' }}>All courses</button>}>
                Continue learning
              </SectionLabel>
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 8 }}>
                <Ring value={nextEnr.progress} size={72} stroke={6} label={`${nextEnr.progress}%`} />
                <div style={{ flex: 1 }}>
                  <div className="th" style={{ fontSize: 18, fontWeight: 600 }}>{L(next.title)}</div>
                  <div style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 2 }}>{next.title.en}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, color: 'var(--sage-deep)', fontSize: 13.5, fontWeight: 500 }}>
                    <Icon name="play" size={14} fill /> Next: {next.lessons.find((l) => !l.done)?.title.en}
                  </div>
                </div>
                <button className="btn btn-primary" onClick={() => navigate(`/learn/catalog/${next.id}`)}>
                  {nextEnr.progress ? 'Resume' : 'Start'} <Icon name="arrowR" size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

          {todoItems.length > 0 && (
          <div className="card" style={{ padding: '14px 4px' }}>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              {todoItems.map((x, i) => (
                <button key={x.l} onClick={() => navigate('/learn/learning')} style={{ all: 'unset', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', gap: 11, padding: '8px 14px', borderRight: i < todoItems.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
                  <span style={{ width: 38, height: 38, borderRadius: 11, flex: 'none', background: `var(--${x.t}-soft)`, color: `var(--${x.t}-deep)`, border: '1.5px solid var(--ink)', display: 'grid', placeItems: 'center' }}>
                    <Icon name={x.ic} size={18} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.05 }}>{x.n}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1, whiteSpace: 'nowrap' }}>{x.l}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          )}

          {featured && (
            <button onClick={() => navigate(`/learn/catalog/${featured.id}`)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
              <div className="card lift" style={{ overflow: 'hidden' }}>
                <div style={{ aspectRatio: '3 / 1', background: featured.coverUrl ? `${featured.cover} center/cover no-repeat url("${featured.coverUrl}")` : featured.cover }} />
                <div style={{ padding: 24 }}>
                  <div className="eyebrow" style={{ color: 'var(--sage-deep)' }}>Start here · เริ่มที่นี่</div>
                  <div className="th" style={{ fontSize: 20, fontWeight: 600, marginTop: 8, lineHeight: 1.3 }}>{L(featured.title)}</div>
                  <div style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 2 }}>{featured.title.en}</div>
                  <p style={{ margin: '12px 0 0', fontSize: 14.5, lineHeight: 1.55, color: 'var(--ink-2)' }}>{featured.summary}</p>
                  <div className="btn btn-primary" style={{ marginTop: 18, display: 'inline-flex' }}>
                    View course <Icon name="arrowR" size={16} />
                  </div>
                </div>
              </div>
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="card" style={{ padding: 24, background: 'linear-gradient(135deg, var(--sage-tint), var(--card) 70%, var(--spark-tint))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <span className="eyebrow">Certificate status</span>
              <Icon name="shield" size={18} style={{ color: 'var(--sage)' }} />
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <Ring value={certPct} size={58} stroke={6} label={`${validCerts}/${certs.length}`} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{validCerts} valid</div>
                {expiringCerts > 0 && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--spark-deep)', fontWeight: 500, marginTop: 3 }}>
                    <Icon name="clock" size={13} /> {expiringCerts} expiring soon
                  </div>
                )}
              </div>
            </div>
            <button className="btn btn-soft btn-block" style={{ marginTop: 18 }} onClick={() => navigate('/learn/wallet')}>
              Open wallet <Icon name="arrowR" size={15} />
            </button>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span className="eyebrow">Upcoming onsite</span>
              <Icon name="cal" size={18} style={{ color: 'var(--slate)' }} />
            </div>
            {upcoming ? (
              <>
                <div className="th" style={{ fontSize: 15, fontWeight: 600 }}>{L(upcoming.title)}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 13, color: 'var(--ink-2)' }}>
                  <Icon name="clock" size={14} style={{ color: 'var(--ink-3)', flex: 'none' }} /> {upcoming.seminar}
                </div>
                {attended ? (
                  <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: 'var(--sage-deep)', padding: 11, background: 'var(--sage-tint)', borderRadius: 10 }}>
                    <Icon name="check" size={15} /> Checked in · เช็คอินแล้ว
                  </div>
                ) : checkinOpen ? (
                  <button className="btn btn-primary btn-block" style={{ marginTop: 16 }} onClick={() => navigate(`/learn/course/${upcoming.id}/attendance`)}>
                    <Icon name="qr" size={16} /> Check in
                  </button>
                ) : (
                  <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', fontSize: 12.5, color: 'var(--ink-3)', padding: 11, background: 'var(--paper-2)', borderRadius: 10 }}>
                    <Icon name="lock" size={14} /> Check-in opens on the session day
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>No onsite sessions scheduled — you're all caught up.</div>
            )}
          </div>
        </div>
      </div>

      {restRec.length > 0 && (
        <div style={{ marginTop: 30 }}>
          <SectionLabel>Recommended for you</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 18 }}>
            {restRec.map((c) => (
              <CourseCard key={c.id} course={c} onClick={() => navigate(`/learn/catalog/${c.id}`)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
